import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Query } from '../../../definitions/query';
import { composeQueryString } from '../../Query';

interface QueryHelper {
  getAllQueryStringParams: () => Query;
  getQueryStringParam: (key: string) => string;
  updateQuery: (queryParams: Query, replace?: boolean) => void;
}

const useQuery = (): QueryHelper => {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Utility method for transforming all query string values into
   * a key-value pair object. This is useful because we may not explicitly know
   * what values come in the URL.
   */
  const getAllQueryStringParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const paramsKeys = Array.from(params.keys());

    const paramsValues: Query = {};
    paramsKeys.reduce((pV, k) => {
      // eslint-disable-next-line no-param-reassign
      pV[k] = params.get(k);
      return pV;
    }, paramsValues);

    return paramsValues;
  }, [location]);

  const getQueryStringParam = useCallback(
    (key: string) => {
      const params = new URLSearchParams(location.search || '');
      return params.get(key) || '';
    },
    [location]
  );

  const updateQuery = useCallback(
    (queryParams: Query, replace = false) => {
      navigate({ search: composeQueryString(queryParams) }, { replace });
    },
    [navigate]
  );

  const helper = useMemo(
    () => ({ getAllQueryStringParams, getQueryStringParam, updateQuery }),
    [getAllQueryStringParams, getQueryStringParam, updateQuery]
  );

  return helper;
};

export default useQuery;
